/**
 * FormDatePicker Component
 * 
 * Standardized date picker component for forms using MUI DatePicker.
 * 
 * @example
 * <FormDatePicker
 *   name="startDate"
 *   label="Start Date"
 *   value={startDate}
 *   onChange={(date) => setStartDate(date)}
 * />
 */

import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, FormControl, FormHelperText } from '@mui/material';
import type { Locale } from 'date-fns';

export interface FormDatePickerProps {
  /** Field name */
  name: string;
  /** Field label */
  label: string;
  /** Selected date value */
  value: Date | null;
  /** Change handler */
  onChange: (date: Date | null) => void;
  /** Error message */
  errorMessage?: string;
  /** Helper text */
  helperText?: string;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Disable past dates */
  disablePast?: boolean;
  /** Disable future dates */
  disableFuture?: boolean;
  /** Whether field is required */
  required?: boolean;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Optional date-fns locale for the calendar (e.g. he/ar for RTL forms). */
  adapterLocale?: Locale;
  /** Display/parse format. Defaults to day/month/year (dd/MM/yyyy). */
  format?: string;
  /** Label sits above the field instead of inside the outline. */
  stackedLabel?: boolean;
}

export const FormDatePicker: React.FC<FormDatePickerProps> = ({
  name,
  label,
  value,
  onChange,
  errorMessage,
  helperText,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  required,
  disabled,
  fullWidth = true,
  adapterLocale,
  format = 'dd/MM/yyyy',
  stackedLabel = false,
}) => {
  const hasError = Boolean(errorMessage);

  const picker = (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <FormControl fullWidth={fullWidth} error={hasError} data-field={name}>
        <DatePicker
          label={stackedLabel ? undefined : label}
          value={value}
          onChange={onChange}
          format={format}
          minDate={minDate}
          maxDate={maxDate}
          disablePast={disablePast}
          disableFuture={disableFuture}
          disabled={disabled}
          slotProps={{
            textField: {
              name,
              required: stackedLabel ? false : required,
              error: hasError,
              fullWidth,
              placeholder: 'dd/mm/yyyy',
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: stackedLabel ? '8px' : 2,
                  ...(stackedLabel ? { height: 44, fontSize: 16, backgroundColor: '#fff' } : {}),
                },
                ...(stackedLabel
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1C1B1A' },
                      '& .MuiOutlinedInput-notchedOutline legend': { maxWidth: 0 },
                    }
                  : {}),
              },
            },
            // Keep the calendar popper above custom app modals (which use
            // z-index 9999). Without this the date picker opens behind the
            // modal and the date cannot be selected.
            popper: {
              sx: { zIndex: 10000 },
            },
          }}
        />
        {(hasError || helperText) && (
          <FormHelperText>{hasError ? errorMessage : helperText}</FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );

  if (!stackedLabel) return picker;

  return (
    <Box>
      <Box
        component="label"
        sx={{ display: 'block', mb: '6px', fontSize: 14, fontWeight: 600, color: '#1C1B1A' }}
      >
        {label}
        {required && <Box component="span" sx={{ color: '#A8213A' }}> *</Box>}
      </Box>
      {picker}
    </Box>
  );
};

export default FormDatePicker;
